import {BUILDING_DUST, BUILDING_DUST_TEXTURES, DUST_TEXTURE_SIZE} from '../../configs/buildingDustAnimation';

/**
 * Описание залпа пыли при размещении жетона: набор клубов с готовыми
 * координатами, размерами и таймингами. Чистая функция без Phaser и DOM — по
 * этому описанию рисует HexGridRenderer.showBuildingPlacementDust.
 *
 * Эффект: жетон бьет по земле ВСЕЙ своей площадью — клубы поднимаются по всему
 * гексу и резко расходятся наружу, затухая. Клубы не вращаются: с вращением
 * залп выглядит маленьким торнадо, а не ударом тяжелого объекта о землю.
 *
 * Круг делится между клубами поровну (каждому своя доля плюс разброс), поэтому
 * в разлете не бывает дыр, но два залпа подряд все равно не похожи: у каждого
 * свой поворот веера, свои точки рождения, дальности, размеры, прозрачности и
 * текстуры.
 *
 * Дальность считается не от точки рождения, а до общего радиуса волны: клуб у
 * кромки жетона пролетает меньше, клуб из середины — больше, и пыль уходит
 * наружу одной волной, а не рваными выбросами.
 *
 * В состояние партии случайность не попадает: эффект целиком визуальный, у
 * каждого клиента свой (детерминизм записей матчей не задет).
 */

const randomRange = (rng, min, max) => min + rng() * (max - min);
const randomInt = (rng, min, max) => Math.floor(randomRange(rng, min, max + 1));
const pickIndex = (rng, length) => Math.min(length - 1, Math.floor(rng() * length));

/**
 * @param {Object} params
 * @param {number} params.hexSize — размер ячейки: от него считаются все размеры
 * @param {number} [params.intensity] — «тяжесть» жетона (дорога легче квартала)
 * @param {Function} [params.rng] — источник случайности (в тестах — заглушка)
 * @param {string[]} [params.textureKeys] — доступные текстуры (в сцене
 *   отфильтрованы по реально загруженным)
 * @returns {{puffs: Object[], baseAngleDeg: number}}
 */
export const buildBuildingDustBurst = ({
  hexSize,
  intensity = 1,
  rng = Math.random,
  textureKeys = BUILDING_DUST_TEXTURES.map((texture) => texture.key),
} = {}) => {
  if (!hexSize || hexSize <= 0 || textureKeys.length === 0) {
    return {puffs: [], baseAngleDeg: 0};
  }

  // Поворот всего веера: круг у каждого залпа начинается в своем месте
  const baseAngleDeg = randomRange(rng, 0, 360);
  const puffCount = randomInt(rng, BUILDING_DUST.puffCount.min, BUILDING_DUST.puffCount.max);
  const sectorDeg = 360 / puffCount;
  const jitterDeg = sectorDeg * BUILDING_DUST.angleJitterShare;

  const puffs = [];
  for (let index = 0; index < puffCount; index += 1) {
    // Текстура: второму клубу даем заведомо другую картинку, иначе залп
    // иногда целиком собирается из одной и выглядит однообразно
    let textureKey = textureKeys[pickIndex(rng, textureKeys.length)];
    if (index === 1 && textureKeys.length > 1 && textureKey === puffs[0].textureKey) {
      const otherKeys = textureKeys.filter((key) => key !== textureKey);
      textureKey = otherKeys[pickIndex(rng, otherKeys.length)];
    }

    const angleDeg = baseAngleDeg + index * sectorDeg + randomRange(rng, -jitterDeg, jitterDeg);
    const angleRad = (angleDeg * Math.PI) / 180;
    // Точка рождения — по всей площади гекса. Корень дает равномерную
    // плотность: без него клубы сбиваются к центру
    const spawnRadius = hexSize * BUILDING_DUST.spawnRadiusFactor * Math.sqrt(rng());
    // Куда доходит волна, считая от ЦЕНТРА ячейки
    const endRadius = hexSize * randomRange(
      rng, BUILDING_DUST.endRadiusFactor.min, BUILDING_DUST.endRadiusFactor.max
    ) * intensity;
    const driftDistance = Math.max(hexSize * BUILDING_DUST.minDriftFactor * intensity, endRadius - spawnRadius);
    const lift = hexSize * randomRange(rng, BUILDING_DUST.liftFactor.min, BUILDING_DUST.liftFactor.max) * intensity;

    const scaleOf = (factorRange) => hexSize * randomRange(rng, factorRange.min, factorRange.max)
      * intensity / DUST_TEXTURE_SIZE;
    const startScale = scaleOf(BUILDING_DUST.startScaleFactor);
    const endScale = Math.max(startScale * 1.15, scaleOf(BUILDING_DUST.endScaleFactor));

    puffs.push({
      textureKey,
      // Клуб рождается там, где жетон ударил по земле, — в своей части гекса
      offsetX: Math.cos(angleRad) * spawnRadius,
      offsetY: Math.sin(angleRad) * spawnRadius * BUILDING_DUST.groundSquash
        + hexSize * BUILDING_DUST.baseOffsetYFactor,
      // Уходит наружу по тому же лучу, на котором родился
      driftX: Math.cos(angleRad) * driftDistance,
      // По вертикали разлет сплюснут — пыль стелется по земле, а не летит
      // шаром, — и ко всему добавляется небольшой подъем
      driftY: Math.sin(angleRad) * driftDistance * BUILDING_DUST.groundSquash - lift,
      startScale,
      endScale,
      // Клуб виден сразу и дальше только затухает
      startAlpha: randomRange(rng, BUILDING_DUST.peakAlpha.min, BUILDING_DUST.peakAlpha.max),
      // Поворот текстуры разовый, на старте: вращения за время жизни нет
      angleDeg: randomRange(rng, 0, 360),
      flipX: rng() < 0.5,
      delay: Math.round(randomRange(rng, BUILDING_DUST.delay.min, BUILDING_DUST.delay.max)),
      duration: Math.round(randomRange(rng, BUILDING_DUST.duration.min, BUILDING_DUST.duration.max)),
    });
  }

  return {puffs, baseAngleDeg};
};
